/**
 * useEmailReply — the reply-to-email behavior behind every reply surface.
 *
 * Extracted verbatim from `EmailReplyButton` so the same account-matching,
 * CC-exclusion, subject-prefixing, and provenance-stamping drives BOTH the
 * rendered buttons (timeline row, conversation foot, expanded cards) and the
 * record header's action-bar entry (`useEmailThreadHeaderActions`), which
 * needs the behavior without the button.
 *
 * `canReply` gates rendering: false when a valid reply can't be constructed
 * (no thread id, no sender address, no org/user context).
 */

import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { useAuthInfo } from '@/allauth_auth/hooks.js';
import { useProposalSidebarOptional } from '@/contexts/ActionSidebarContext';
import {
  type ConnectedAccount,
  useConnectedAccounts,
} from '@/hooks/integrations/useConnectedAccounts';
import { extractEmail } from '@/lib/crm/utils/email-address';
import { logger } from '@/lib/logger';
import { quickEmailProposal } from '@/lib/proposals/core/ActionBuilder';
import { useCreateProposal } from '@/lib/proposals/query/mutations';

import type { EmailPayload } from '../types';

const PROPOSAL_TITLE_MAX_LENGTH = 255;

function buildReplyTitle(subject: string): string {
  const prefix = 'Reply to: ';
  const fullTitle = `${prefix}${subject}`;
  if (fullTitle.length <= PROPOSAL_TITLE_MAX_LENGTH) {
    return fullTitle;
  }
  // Reserve room for the ellipsis indicator (1 char: '…').
  const maxSubjectLen = PROPOSAL_TITLE_MAX_LENGTH - prefix.length - 1;
  return `${prefix}${subject.slice(0, maxSubjectLen)}…`;
}

export interface UseEmailReplyArgs {
  /** The thread entity the email event belongs to (proposal source id). */
  entityId: string | undefined;
  /**
   * The timeline event id of the message being replied to. Stamped into the
   * proposal's source-context provenance (`event_ids`) so source-scoped
   * consumers attribute the reply to the specific message, not just the
   * thread. Timeline event ids are numeric strings; a non-numeric value is
   * skipped rather than sent malformed.
   */
  replyToEventId?: string;
  /** The email event's payload. */
  payload: EmailPayload | undefined;
  /**
   * True when the message being replied to is the viewer's OWN (an
   * `ahoy_email_sent`). Replying to your own message follows the mail-client
   * convention: the draft addresses the message's original To recipients
   * (minus the viewer's own identities) — addressing it to the sender would
   * email yourself. Defaults to false (inbound: draft to the sender).
   */
  isOutbound?: boolean;
  /**
   * Gates the hook's data work (connected-accounts fetch). Callers that
   * evaluate this hook speculatively on every record surface (the header
   * action bar runs it for ALL entity types) MUST pass false for
   * non-email-thread hosts, so opening a contact/company/meeting slideout
   * doesn't fire mailbox-status requests for an action that can never
   * render. Defaults to true — real reply surfaces need the accounts.
   */
  enabled?: boolean;
}

export interface UseEmailReplyResult {
  /** False when a valid reply can't be constructed — render nothing. */
  canReply: boolean;
  /** "Reply all" when other recipients survive self-CC exclusion. */
  label: string;
  /** The email's subject, for aria labels ("email" fallback). */
  subject: string;
  /**
   * True when the email has other recipients beyond the sender and the
   * viewer's own identities — i.e. when Reply (sender only) and Reply all
   * are genuinely different actions worth offering separately.
   */
  hasOtherRecipients: boolean;
  /** Reply keeping the other recipients on CC ("Reply all" semantics). */
  handleReply: () => Promise<void>;
  /** Reply to the sender only — CCs dropped. */
  handleReplySender: () => Promise<void>;
  isCreatingReply: boolean;
}

export function useEmailReply({
  entityId,
  replyToEventId,
  payload,
  isOutbound = false,
  enabled = true,
}: UseEmailReplyArgs): UseEmailReplyResult {
  const { orgId: orgIdParam } = useParams<{ orgId: string }>();
  const orgId = orgIdParam ? Number(orgIdParam) : undefined;
  const { user } = useAuthInfo();
  const { accounts: connectedAccounts } = useConnectedAccounts({
    orgId: orgIdParam,
    autoLoad: !!orgIdParam && enabled,
  });
  // Optional: this hook now runs on every record surface (the header action
  // bar evaluates it for threads on any host), including hosts without a
  // ProposalSidebarProvider (builder panel, isolated tests). No provider →
  // the reply can't be surfaced → `canReply` is false and no surface offers
  // it, instead of throwing.
  const proposalSidebar = useProposalSidebarOptional();
  const createProposal = useCreateProposal(orgId ?? 0);
  const [isCreatingReply, setIsCreatingReply] = useState(false);

  // Reply-eligibility: try to match a connected account whose email appears
  // on the viewer's side of this email — to/cc for a received message (the
  // account it arrived on), the From for the viewer's own sent message (the
  // account it went out from). Best-effort — when no connected account
  // matches (e.g. harbour / test integration emails), we fall back to the
  // current user's email and omit the social account ID. The backend routes
  // the reply based on the thread's provider regardless.

  // Extracted sender address — a string primitive, stable across re-renders
  // when payload?.from is unchanged.
  const fromAddr = payload?.from ? extractEmail(payload.from) : '';
  // Stable key strings: convert to/cc address arrays to '|'-joined strings of
  // extracted emails. Downstream useMemos keyed on these strings are shielded
  // from array-reference identity churn — a new array instance carrying the
  // same addresses produces the same string value, so recomputation is skipped.
  const toAddrsKey = useMemo(
    () =>
      (payload?.to ?? [])
        .map(extractEmail)
        .filter(addr => !!addr)
        .join('|'),
    [payload?.to]
  );
  const ccAddrsKey = useMemo(
    () =>
      (payload?.cc ?? [])
        .map(extractEmail)
        .filter(addr => !!addr)
        .join('|'),
    [payload?.cc]
  );

  // Every address the viewer owns: the logged-in user's email PLUS every
  // connected mailbox address. A reply must exclude all of these — not just
  // the matched sending account — or it can address another mailbox the same
  // viewer owns (a self-send to the wrong recipient set). Keyed as a sorted
  // '|'-joined string so a fresh connectedAccounts array carrying the same
  // addresses doesn't churn the Set identity (and the memos that read it).
  const selfAddressesKey = useMemo(() => {
    const addrs = new Set<string>();
    const userEmail = user?.email?.toLowerCase();
    if (userEmail) {
      addrs.add(userEmail);
    }
    for (const acct of connectedAccounts) {
      const acctEmail = acct.email?.toLowerCase();
      if (acctEmail) {
        addrs.add(acctEmail);
      }
    }
    return Array.from(addrs).sort().join('|');
  }, [connectedAccounts, user?.email]);
  const selfAddresses = useMemo(
    () => new Set(selfAddressesKey.split('|').filter(Boolean)),
    [selfAddressesKey]
  );

  const matchedAccount: ConnectedAccount | undefined = useMemo(() => {
    if (connectedAccounts.length === 0) {
      return undefined;
    }
    let candidates: string[];
    if (isOutbound) {
      candidates = fromAddr ? [fromAddr] : [];
    } else {
      candidates = [toAddrsKey, ccAddrsKey]
        .filter(Boolean)
        .flatMap(k => k.split('|'))
        .filter(Boolean);
    }
    return connectedAccounts.find(
      acct => acct.email && candidates.includes(acct.email.toLowerCase())
    );
  }, [connectedAccounts, isOutbound, fromAddr, toAddrsKey, ccAddrsKey]);

  const createReply = useCallback(
    async (includeCc: boolean) => {
      if (!user?.id || !orgId || !entityId || !proposalSidebar) {
        return;
      }
      const threadId = payload?.thread_id;
      if (!threadId) {
        return;
      }
      setIsCreatingReply(true);
      try {
        // Exclude every viewer identity from recipients so the reply never
        // addresses or self-CCs a mailbox the viewer owns. `selfAddresses`
        // holds the logged-in user's email plus EVERY connected mailbox
        // address — not just the matched sending account — so a reply can't
        // land in another of the viewer's own mailboxes.
        const isSelf = (addr: string) => selfAddresses.has(addr);
        // Inbound: draft to the sender. Outbound (replying to your own
        // message): draft to its original To recipients — the mail-client
        // convention for continuing a thread you spoke last in.
        let replyTo: string[];
        if (isOutbound) {
          // toAddrsKey already contains extracted emails, one per '|' segment.
          replyTo = toAddrsKey
            .split('|')
            .filter(addr => !!addr && !isSelf(addr));
        } else {
          replyTo = fromAddr ? [fromAddr] : [];
        }
        if (replyTo.length === 0) {
          // Nothing valid to address (e.g. an outbound note-to-self) —
          // canReply gates the surfaces, but never send an empty To.
          return;
        }
        // Reply-to-sender-only drops the CCs entirely.
        const replyCc = includeCc
          ? ccAddrsKey.split('|').filter(addr => !!addr && !isSelf(addr))
          : [];
        const baseSubject = payload?.subject ?? '';
        const replySubject = /^re:\s/i.test(baseSubject)
          ? baseSubject
          : `Re: ${baseSubject}`;

        // Provenance: the thread entity plus — when the caller supplied one —
        // the specific message event this reply targets. `event_ids` is the
        // documented numeric provenance field (see SourceContext /
        // source_context_codec.py); stamping it here is what lets
        // source-scoped consumers (withdrawal, provenance display) attribute
        // the reply to the message rather than only the thread.
        const numericReplyEventId =
          replyToEventId != null && replyToEventId !== ''
            ? Number(replyToEventId)
            : Number.NaN;
        const request = quickEmailProposal({
          title: buildReplyTitle(baseSubject),
          description: 'Reply drafted from timeline',
          assignedUserId: user.id,
          sourceTypeName: 'ahoy_timeline_reply',
          sourceId: entityId,
          sourceContext: {
            channel: 'crm_event',
            account_identifier: matchedAccount?.email ?? user.email ?? '',
            entity_ids: [entityId],
            ...(Number.isFinite(numericReplyEventId)
              ? { event_ids: [numericReplyEventId] }
              : {}),
          },
          to: replyTo,
          subject: replySubject,
          body: '',
          threadId,
          cc: replyCc.length > 0 ? replyCc : undefined,
          isReply: true,
          // Provider-specific message target — each mailbox provider keys
          // reply threading off its own id (EMAIL_INTEGRATION_REQUIREMENTS
          // parity rule). Providers without one (harbour, test) fall back to
          // thread-level threading via threadId.
          replyToMessageId:
            payload?.gmail_message_id ?? payload?.outlook_message_id,
          socialAccountId: matchedAccount?.id,
        });
        const proposal = await createProposal.mutateAsync(request);
        proposalSidebar.openProposal(proposal.id);
      } catch (err) {
        logger.error(
          'Failed to create reply proposal from timeline',
          err instanceof Error ? err : undefined,
          { entity_id: entityId }
        );
        toast.error("We couldn't create the reply draft. Try again.");
      } finally {
        setIsCreatingReply(false);
      }
    },
    [
      user,
      orgId,
      payload?.thread_id,
      payload?.subject,
      payload?.gmail_message_id,
      payload?.outlook_message_id,
      entityId,
      replyToEventId,
      isOutbound,
      matchedAccount,
      selfAddresses,
      fromAddr,
      toAddrsKey,
      ccAddrsKey,
      createProposal,
      proposalSidebar,
    ]
  );

  // Two public entry points over one implementation: keep-CCs ("Reply all"
  // semantics — the historical behavior every existing button uses) and
  // sender-only. Wrapped so consumers can hand them straight to onClick
  // without an event object leaking into the includeCc parameter.
  const handleReply = useCallback(() => createReply(true), [createReply]);
  const handleReplySender = useCallback(
    () => createReply(false),
    [createReply]
  );

  // The draft this creates keeps the email's other recipients on CC (see
  // handleReply) — when any survive the self-exclusion, the label says so:
  // "Reply all", the mail-client term for exactly that behavior. A bare
  // "Reply" that silently CCs everyone understates what will happen.
  const repliesToAll = useMemo(() => {
    return ccAddrsKey
      .split('|')
      .some(addr => !!addr && !selfAddresses.has(addr));
  }, [ccAddrsKey, selfAddresses]);

  const threadId = payload?.thread_id;
  // Without a valid target address, the reply proposal would be created with
  // empty `to:` recipients and fail downstream. Gate the action on the
  // direction-appropriate target (inbound: the sender; outbound: at least
  // one non-self To recipient) so no surface offers a reply it can't
  // construct.
  const hasReplyTarget = useMemo(() => {
    if (!isOutbound) {
      return !!fromAddr;
    }
    return toAddrsKey
      .split('|')
      .some(addr => !!addr && !selfAddresses.has(addr));
  }, [isOutbound, fromAddr, toAddrsKey, selfAddresses]);
  const canReply =
    !!threadId &&
    !!orgId &&
    !!user?.id &&
    hasReplyTarget &&
    !!entityId &&
    !!proposalSidebar;

  return {
    canReply,
    label: repliesToAll ? 'Reply all' : 'Reply',
    subject: payload?.subject || 'email',
    hasOtherRecipients: repliesToAll,
    handleReply,
    handleReplySender,
    isCreatingReply,
  };
}
